/* effect_id → drug slug, for rendering an equipped card-effect with its
   responsible skill-tree node's CANONICAL icon instead of a text label.

   DERIVED from the canonical card registry — keep in sync when cards change:
     python3 - <<'PY'
     import json, re
     for c in json.load(open('backend/services/golden_cards_data.json')):
         eid=(c.get('effect') or {}).get('id'); gen=c.get('generic_name')
         slug=re.sub(r'[^a-z0-9]+','_',(gen or '').lower()).strip('_')
         if eid: print(f"  {eid}: '{slug}',")
     PY

   Synthetic keystone surfacing effects (*_keystone) map back to their keystone
   drug so the combat rail renders the canonical keystone glyph/color.

   NOTE: a few equippable effects live in seed_golden_reservoir.py /
   golden_service.py rather than golden_cards_data.json, so the regen snippet
   above does NOT emit them — they must be kept by hand below (marked). The
   combat-type one is `vancomycin_synergistic_strike` (Rifampin's co-equip
   card); without it the combat rail falls back to the raw "Synergistic Strike"
   text label instead of Rifampin's node glyph. */
export const EFFECT_DRUG_SLUG = {
  adalimumab_keystone: 'adalimumab',
  altered_perception: 'dronabinol',
  anxiolytic_tolerance: 'hydroxyzine',
  auxiliary_pathway: 'gabapentin',
  basal_shield: 'insulin_glargine',
  bolus_guard: 'insulin_lispro',
  buffered_recovery: 'sodium_bicarbonate',
  cholinesterase_inhibitor: 'donepezil',
  cocky: 'testosterone',
  cytotoxic_fury: 'cyclophosphamide',
  dopamine_precursor: 'levodopa',
  double_edge: 'digoxin',
  folate_rescue: 'folic_acid',
  glycogen_reserve: 'glucagon',
  hyperfocus: 'methylphenidate',
  immunosuppressant: 'methotrexate',
  indigo_mantle: 'methylene_blue',
  insulin_sensitizer: 'metformin',
  ketone_charges: 'empagliflozin',
  lifesteal: 'warfarin',
  mild_analgesic: 'acetaminophen',
  neural_rewire: 'psilocybin',
  nmda_antagonist: 'memantine',
  open_formulary: 'mannitol',
  ramp_suppression: 'ibuprofen',
  rapid_absorption: 'caffeine',
  revive: 'naloxone',
  second_wind: 'albuterol',
  stimulant_session: 'amphetamine',
  streak_shield: 'propranolol',
  vancomycin_keystone: 'vancomycin',
  metformin_keystone: 'metformin',
  // Reservoir/golden_service effects NOT present in golden_cards_data.json —
  // keep by hand (the regen snippet won't emit them). Rifampin's co-equip
  // combat card is the one that surfaces on the rail.
  vancomycin_synergistic_strike: 'rifampin',
  // Metformin keystone's passive (effect id 'currency_bonus', "Metabolic
  // Efficiency") DOES surface as an equipped chip — unlike the vanc/adali
  // keystones, which carry no surfacing effect. Map it to the metformin keystone
  // so the chip renders the REAL keystone glyph (getKeystoneIconPath → the green
  // branching-circuit line-art, mirroring vanc=sigma / adali=union) instead of
  // falling back to the raw "Metabolic Efficiency" text label.
  // (Lisinopril's 'daily_income_boost' is a non-combat daily-income passive with
  // no node glyph and never surfaces on the combat rail — intentionally unmapped.)
  currency_bonus: 'metformin',
  zero_order: 'phenytoin',
};

/* Node identity color per drug — mirrors the color the drug's node renders in
   the skill tree (skillTreeLayout.js). Tree color precedence there is:
   keystone-affinity accent (connector cards take the parent keystone's accent)
   → warfarin/psilocybin purple → default card_unlock gold. We reproduce that
   here keyed by drug_slug so a combat effect chip reads in its node's color
   instead of a flat amber. Drugs not listed fall back to the card_unlock gold
   default (handlerColor('card_unlock') === '#fbbf24'). Keep in sync if a card's
   keystone cluster placement changes. */
const KEYSTONE_ACCENT = {
  vancomycin: '#ef4444', // red
  metformin: '#34d399',  // green
  adalimumab: '#60a5fa', // blue
};
const CARD_UNLOCK_GOLD = '#fbbf24';
const OFF_TREE_PURPLE = '#a855f7'; // warfarin hub + psilocybin off-tree identity

export const EFFECT_NODE_COLOR = {
  // Adalimumab (blue) affinity cluster
  albuterol: KEYSTONE_ACCENT.adalimumab,
  methylphenidate: KEYSTONE_ACCENT.adalimumab,
  propranolol: KEYSTONE_ACCENT.adalimumab,
  // Metformin (green) affinity cluster
  sodium_bicarbonate: KEYSTONE_ACCENT.metformin,
  glucagon: KEYSTONE_ACCENT.metformin,
  empagliflozin: KEYSTONE_ACCENT.metformin,
  // Vancomycin (red) affinity cluster
  digoxin: KEYSTONE_ACCENT.vancomycin,
  phenytoin: KEYSTONE_ACCENT.vancomycin,
  rifampin: KEYSTONE_ACCENT.vancomycin, // synergistic_strike co-equip card
  // Off-tree / hub purple
  warfarin: OFF_TREE_PURPLE,
  psilocybin: OFF_TREE_PURPLE,
  // Keystones themselves (never surface as effect chips, mapped for completeness)
  vancomycin: KEYSTONE_ACCENT.vancomycin,
  metformin: KEYSTONE_ACCENT.metformin,
  adalimumab: KEYSTONE_ACCENT.adalimumab,
};

/* Color a drug's node identity. Falls back to the card_unlock gold the tree
   uses for plain unlock nodes. */
export function effectNodeColor(slug) {
  if (!slug) return CARD_UNLOCK_GOLD;
  return EFFECT_NODE_COLOR[String(slug).toLowerCase()] || CARD_UNLOCK_GOLD;
}

/* Title-case a drug slug for display in the hover tooltip's tag element, so it
   reads like the node label the user sees in the tree (sodium_bicarbonate →
   "Sodium Bicarbonate"). */
export function drugDisplayName(slug) {
  if (!slug) return '';
  return String(slug)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/* Keystone-augment-specific tooltip copy per effect, DERIVED from each card's
   `keystone_synergy` in backend/services/golden_cards_data.json (slug +
   synergy_copy). The base effect comes from the backend `effect.description`;
   THIS is the separate "while <keystone> is equipped …" augment section, shown
   only when the active keystone === keystone_slug. Keep in sync when synergy
   copy changes. Regenerate base form:
     python3 - <<'PY'
     import json
     for c in json.load(open('backend/services/golden_cards_data.json')):
         eff=c.get('effect') or {}; ks=c.get('keystone_synergy') or {}
         if eff.get('id') and ks.get('synergy_copy') and ks.get('keystone_slug'):
             print(eff['id'], ks['keystone_slug'], ks['synergy_copy'])
     PY
*/
export const EFFECT_AUGMENT = {
  // Lifesteal is intentionally absent: its 15% heal and INR liability are the
  // Warfarin base effect under every keystone, not a keystone-gated augment.
  // Vancomycin family — synergy logic is hardcoded in System B
  // (batch_combat_engine._compute_vanc_crit + submit_answer + the :5748 dispense
  // gate), NOT in golden_cards_data.json. Keep these 3 in sync with that engine
  // code, not the (stale) JSON. See wiki/reference/keystone-synergy-two-systems.md.
  double_edge: { keystone_slug: 'vancomycin', lines: ['NARROW THERAPEUTIC INDEX: under Vancomycin, a CORRECT precise-dosing beat amplifies the batch crit ×1.4 (stacks multiplicatively with Phenytoin); a WRONG precise-dosing beat self-inflicts 5 narrow-TI toxicity damage.'] },
  zero_order: { keystone_slug: 'vancomycin', lines: ['On Vancomycin: each banked Phenytoin stack amplifies the precise-dosing batch crit by +50%. Effective crit = 2 × (1 + N×0.5) = 2 + N. A wrong answer crashes the stacks.'] },
  vancomycin_synergistic_strike: { keystone_slug: 'vancomycin', lines: ['SYNERGISTIC STRIKE: under Vancomycin, precise-dosing beats both DISPENSE their damage AND double the batch, instead of being a pure ×2 trigger on the prior batch. The true-upgrade path.'] },
  streak_shield: { keystone_slug: 'adalimumab', lines: ['BETA-BLOCKADE: while Adalimumab is equipped, spend a dodge token to survive a streak-break once your shield is already gone.'] },
  second_wind: { keystone_slug: 'adalimumab', lines: ['On partial-credit Adalimumab answers: second_wind arms with a bonus_mult scaled by how partial. Bigger stumble = bigger rebound (2.0×–3.0× cap).'] },
  glycogen_reserve: { keystone_slug: 'metformin', lines: ['GLUCAGON MOBILIZATION: while Metformin is equipped, each correct green keystone beat banks +5 Glycogen. The Algorithm’s heal economy fills the reserve fast.'] },
  hyperfocus: { keystone_slug: 'adalimumab', lines: ['LOCKED IN: while Adalimumab is equipped, every 3rd consecutive correct banks +1 dodge token. Perfect precision becomes defense.'] },
  buffered_recovery: { keystone_slug: 'metformin', lines: ['On Metformin green-correct: per-round heal can overshield (no cap, no decay) AND clears 2 Lactic stacks. Stacks with universal 5/4/3/2/1 end-of-round heal.'] },
  // Empagliflozin: KETOGENIC SHIFT + the bicarb-exemption note (Edward 2026-06-04).
  ketone_charges: { keystone_slug: 'metformin', lines: [
    'KETOGENIC SHIFT: while Metformin is equipped, riding Lactic acidosis speeds ketosis (+1 extra charge on Lactic rounds), and the burst clears all your Lactic stacks.',
    'Sodium Bicarbonate healing does not count against Ketone build-up.',
  ] },
};

/* Resolve the keystone-augment section for an equipped effect, or null. */
export function effectAugment(effect) {
  if (!effect || !effect.id) return null;
  return EFFECT_AUGMENT[effect.id] || null;
}

/* Resolve the drug slug responsible for an equipped effect. Prefers an
   explicit slug if the backend ever attaches one, then the effect_id map,
   then a normalized drug_name fallback. Returns null if nothing resolves. */
export function effectToDrugSlug(effect) {
  if (!effect) return null;
  if (effect.drug_slug) return String(effect.drug_slug).toLowerCase();
  const byId = effect.id ? EFFECT_DRUG_SLUG[effect.id] : null;
  if (byId) return byId;
  if (effect.drug_name) {
    return String(effect.drug_name).trim().toLowerCase().replace(/[\s-]+/g, '_');
  }
  return null;
}
